import InventoryItem, { RecipeIngredient } from './Inventory';

export type Recipe = {
  id: string;
  name: string;
  style: string;
  description?: string;
  ingredients: RecipeIngredient[]; // Array of ingredients used
  batchSize: number;
  steps: Step[];
  color?: string;
  baseRecipe?: boolean;
  slug: string;
  defaultAbv?: number; // Default ABV if not specified in batch
  experimental?: boolean; // Whether the recipe is experimental
  containers?: RecipeContainer[];
};

// --- Packaging (per recipe) ---

/** A component consumed ONCE PER CONTAINER (e.g., 1 label, 1 cap, 1 carb drop). */
export type PerContainerComponent = {
  item: InventoryItem; // must be an item with perContainer === true
  amountPerContainer: number; // e.g., 1 label, 1 cap, 0.5 drop (if halved)
  notes?: string;
  // Optional: allow overrides for display or unit tweaks if needed in UI
  unitOverride?: string; // e.g., "pc", "drop"
};

/** A container option for this recipe (e.g., 0.33 L can, 0.75 L bottle). */
export type RecipeContainer = {
  id?: string;
  containerItem: InventoryItem; // isContainer === true
  sizeL: number; // exact container volume (e.g., 0.33, 0.75)
  components: PerContainerComponent[]; // labels, caps, drops, etc.
};

export type Step = {
  type?: StepType; // Type of step
  name: string; // Name of the step
  description?: string; // Description of the step
  duration: number;
  temperature?: number;
  gravity?: number;
  pH?: number;
  notes?: string; // Notes about the step
  start?: Date; // Date when the step started
  end?: Date; // Date when the step ended
};

export type StepType =
  // Prep & hygiene
  | 'sanitize' // sanitize vessels, tools (hygiene)
  | 'clean' // clean equipment/area (pre/post step)

  // Ingredient prep
  | 'thaw' // defrost fruit/juice
  | 'crush_puree' // crush, puree, or mill
  | 'juice_press' // press fruit/berries
  | 'heat' // warm honey/must; pasteurize
  | 'cool' // chill must/wort/juice
  | 'mix' // dissolve honey; combine ingredients
  | 'oxygenate' // aerate/oxygenate must
  | 'enzyme_addition' // pectic amylase, glucoamylase, etc.
  | 'nutrient_addition' // SNA/TOSNA, staggered nutrients
  | 'acid_adjust' // adjust TA/pH (acid/base)
  | 'tannin_add' // add tannins/structure
  | 'spice_add' // cinnamon, vanilla, etc.
  | 'fruit_add' // primary fruit addition
  | 'oak_add' // cubes/spirals/staves/chips
  | 'prepare' // prepare ingredients

  // Fermentation & control
  | 'yeast_rehydrate' // rehydrate with Go-Ferm, etc.
  | 'yeast_pitch' // inoculate must
  | 'fermentation_primary'
  | 'degassing' // CO₂ release during early ferment
  | 'step_feeding' // honey additions during ferment
  | 'temperature_control' // ramp/hold/adjust ferment temp
  | 'fermentation_secondary' // post‑primary, fruit-on, etc.

  // Post‑ferment handling & clarification
  | 'racking' // transfer off lees/fruit
  | 'maceration' // soak/infuse (fruit/herb/oak)
  | 'dry_addition' // dry spice/fruit hop-style
  | 'fining' // bentonite, sparkolloid, chitosan
  | 'filtering' // filter polish; plate/bag filter
  | 'straining' // coarse separation (bags, screens)
  | 'cold_crash' // drop temp to clarify
  | 'stabilize' // K-meta/K-sorbate or thermal
  | 'backsweeten' // add honey/juice after stabilize
  | 'blend' // blend lots/batches to target
  | 'maturation' // bulk age/condition in tank/carboy
  | 'oak_age' // age on oak (distinct from add)

  // Packaging
  | 'carbonation_prime' // priming sugar/honey for bottle carb
  | 'force_carb' // force carbonate (draft/keg)
  | 'bottling' // package into bottles
  | 'conditioning' // bottle/keg conditioning
  | 'labeling' // apply labels/caps/lot marks

  // QC / checkpoints
  | 'measurement' // gravity/pH/temp/ABV checks
  | 'sensory_check' // taste/aroma evaluation
  | 'qa_check' // quality assurance checks

  // Enjoy
  | 'enjoy' // enjoy the final product

  // Catch‑all
  | 'misc';

export const stepTypeLabels = {
  sanitize: 'Sanitize',
  clean: 'Clean',
  thaw: 'Thaw',
  crush_puree: 'Crush/Puree',
  juice_press: 'Juice Press',
  heat: 'Heat',
  cool: 'Cool',
  mix: 'Mix',
  oxygenate: 'Oxygenate',
  enzyme_addition: 'Enzyme Addition',
  nutrient_addition: 'Nutrient Addition',
  acid_adjust: 'Acid Adjust',
  tannin_add: 'Tannin Add',
  spice_add: 'Spice Add',
  fruit_add: 'Fruit Add',
  oak_add: 'Oak Add',
  prepare: 'Prepare',
  yeast_rehydrate: 'Yeast Rehydrate',
  yeast_pitch: 'Yeast Pitch',
  fermentation_primary: 'Fermentation (Primary)',
  degassing: 'Degassing',
  step_feeding: 'Step Feeding',
  temperature_control: 'Temperature Control',
  fermentation_secondary: 'Fermentation (Secondary)',
  racking: 'Racking',
  maceration: 'Maceration',
  dry_addition: 'Dry Addition',
  fining: 'Fining',
  filtering: 'Filtering',
  straining: 'Straining',
  cold_crash: 'Cold Crash',
  stabilize: 'Stabilize',
  backsweeten: 'Backsweeten',
  blend: 'Blend',
  maturation: 'Maturation',
  oak_age: 'Oak Age',
  carbonation_prime: 'Carbonation Prime',
  force_carb: 'Force Carbonate',
  bottling: 'Bottling',
  conditioning: 'Conditioning',
  labeling: 'Labeling',
  measurement: 'Measurement',
  sensory_check: 'Sensory Check',
  qa_check: 'QA Check',
  misc: 'Miscellaneous',
  enjoy: 'Enjoy',
} satisfies Record<StepType, string>;

export const getStepTypeLabel = (t: StepType) => stepTypeLabels[t];
export const withDefaultType = (s: Step): Step & { type: StepType } => ({
  ...s,
  type: s.type ?? 'misc',
});

export const StepTypeValues = Object.keys(stepTypeLabels) as StepType[];

export const isStepType = (v: string): v is StepType => (stepTypeLabels as Record<string, string>)[v] !== undefined;

// (Optional) grouped options for a select:
export const stepTypeOptions = [
  {
    label: 'Prep & Hygiene',
    options: [
      { value: 'sanitize', label: stepTypeLabels.sanitize },
      { value: 'clean', label: stepTypeLabels.clean },
    ],
  },
  {
    label: 'Ingredient Prep',
    options: [
      { value: 'thaw', label: stepTypeLabels.thaw },
      { value: 'crush_puree', label: stepTypeLabels.crush_puree },
      { value: 'juice_press', label: stepTypeLabels.juice_press },
      { value: 'heat', label: stepTypeLabels.heat },
      { value: 'cool', label: stepTypeLabels.cool },
      { value: 'mix', label: stepTypeLabels.mix },
      { value: 'oxygenate', label: stepTypeLabels.oxygenate },
      { value: 'enzyme_addition', label: stepTypeLabels.enzyme_addition },
      { value: 'nutrient_addition', label: stepTypeLabels.nutrient_addition },
      { value: 'acid_adjust', label: stepTypeLabels.acid_adjust },
      { value: 'tannin_add', label: stepTypeLabels.tannin_add },
      { value: 'spice_add', label: stepTypeLabels.spice_add },
      { value: 'fruit_add', label: stepTypeLabels.fruit_add },
      { value: 'oak_add', label: stepTypeLabels.oak_add },
      { value: 'prepare', label: stepTypeLabels.prepare },
    ],
  },
  {
    label: 'Fermentation & Control',
    options: [
      { value: 'yeast_rehydrate', label: stepTypeLabels.yeast_rehydrate },
      { value: 'yeast_pitch', label: stepTypeLabels.yeast_pitch },
      { value: 'fermentation_primary', label: stepTypeLabels.fermentation_primary },
      { value: 'degassing', label: stepTypeLabels.degassing },
      { value: 'step_feeding', label: stepTypeLabels.step_feeding },
      { value: 'temperature_control', label: stepTypeLabels.temperature_control },
      { value: 'fermentation_secondary', label: stepTypeLabels.fermentation_secondary },
    ],
  },
  {
    label: 'Post‑Fermentation',
    options: [
      { value: 'racking', label: stepTypeLabels.racking },
      { value: 'maceration', label: stepTypeLabels.maceration },
      { value: 'dry_addition', label: stepTypeLabels.dry_addition },
      { value: 'fining', label: stepTypeLabels.fining },
      { value: 'filtering', label: stepTypeLabels.filtering },
      { value: 'straining', label: stepTypeLabels.straining },
      { value: 'cold_crash', label: stepTypeLabels.cold_crash },
      { value: 'stabilize', label: stepTypeLabels.stabilize },
      { value: 'backsweeten', label: stepTypeLabels.backsweeten },
      { value: 'blend', label: stepTypeLabels.blend },
      { value: 'maturation', label: stepTypeLabels.maturation },
      { value: 'oak_age', label: stepTypeLabels.oak_age },
    ],
  },
  {
    label: 'Packaging',
    options: [
      { value: 'carbonation_prime', label: stepTypeLabels.carbonation_prime },
      { value: 'force_carb', label: stepTypeLabels.force_carb },
      { value: 'bottling', label: stepTypeLabels.bottling },
      { value: 'conditioning', label: stepTypeLabels.conditioning },
      { value: 'labeling', label: stepTypeLabels.labeling },
    ],
  },
  {
    label: 'QC',
    options: [
      { value: 'measurement', label: stepTypeLabels.measurement },
      { value: 'sensory_check', label: stepTypeLabels.sensory_check },
      { value: 'misc', label: stepTypeLabels.misc },
    ],
  },

  // Enjoy
  {
    label: 'Complete',
    options: [{ value: 'enjoy', label: stepTypeLabels.enjoy }],
  },
];

// Simple diacritic strip
const stripDiacritics = (s: string) => s.normalize?.('NFD').replace(/[\u0300-\u036f]/g, '') ?? s;

/**
 * Normalise text for matching:
 * - lowercases
 * - strips diacritics
 * - maps common UK spellings and plural/inflection variants to a canonical form
 */
const normalise = (s: string) => {
  let t = stripDiacritics(s.toLowerCase());

  // UK -> US (and other canonical) mappings
  const replacements: Array<[RegExp, string]> = [
    // ise/isation -> ize/ization
    [/\bsanitise\b/g, 'sanitize'],
    [/\bsanitising\b/g, 'sanitizing'],
    [/\bsanitised\b/g, 'sanitized'],
    [/\bsterilise\b/g, 'sterilize'],
    [/\bsterilisation\b/g, 'sterilization'],
    [/\bpasteurise\b/g, 'pasteurize'],
    [/\bpasteurised\b/g, 'pasteurized'],
    [/\bpasteurising\b/g, 'pasteurizing'],

    // sulph- -> sulf-
    [/\bsulphite\b/g, 'sulfite'],
    [/\bsulphites\b/g, 'sulfites'],
    [/\bsulphur\b/g, 'sulfur'],

    // litre/label/colour (mainly for descriptions)
    [/\blitre(s)?\b/g, 'liter$1'],
    [/\blitre(s)?/g, 'liter$1'],
    [/\blabelling\b/g, 'labeling'],
    [/\blabelled\b/g, 'labeled'],
    [/\bcolour(s|ed|ing)?\b/g, 'color$1'],

    // pH variants spacing
    [/\bp ?h\b/g, 'ph'],

    // common variants/typos
    [/\bdegas(s|sing)?\b/g, 'degassing'], // degas/degassing
    [/\bgo ?-? ?ferm\b/g, 'go-ferm'], // go ferm/go-ferm
    [/\bforce ?carbonat(e|ion)\b/g, 'force-carbonate'],
    [/\bcold ?crash(ing)?\b/g, 'cold crash'],
    [/\boak ?age(ing)?\b/g, 'oak age'],
    [/\bstep ?feed(ing)?\b/g, 'step feeding'],
    [/\bjuice ?press\b/g, 'juice press'],
    [/\bdry ?addition\b/g, 'dry addition'],
  ];

  for (const [re, rep] of replacements) t = t.replace(re, rep);
  return t;
};

export function suggestStepType(s: Pick<Step, 'name' | 'description'>): StepType | undefined {
  const nameRaw = s.name ?? '';
  const descRaw = s.description ?? '';
  const name = normalise(nameRaw);
  const desc = normalise(descRaw);
  const txt = `${name} ${desc}`;

  // --- strong name anchors (return immediately on match) ---
  const NAME_MAP: Array<[RegExp, StepType]> = [
    [/\bsanit(i[sz])ation\b/, 'sanitize'],
    [/\bingredients? prepar/i, 'misc'],
    [/\bcreating the must\b/, 'mix'],
    [/\badding (initial )?ingredients?\b/, 'fruit_add'],
    [/\byeast (pitch|inoculation)\b/, 'yeast_pitch'],
    [/\bprimary fermentation\b/, 'fermentation_primary'],
    [/\bremoval of solids\b/, 'straining'],
    [/\bracking\b/, 'racking'],
    [/\bsecondary fermentation\b/, 'fermentation_secondary'],
    [/\bfilter(ing)?\b/, 'filtering'],
    [/\bbottling\b/, 'bottling'],
    [/\bbottle aging\b/, 'conditioning'],
    [/\bfinal testing\b/, 'enjoy'],
  ];
  for (const [re, t] of NAME_MAP) {
    if (re.test(name)) return t;
  }

  // --- helpers ---
  const any = (rs: RegExp[], t: string) => rs.some((r) => r.test(t));
  const has = (r: RegExp) => r.test(txt);

  // Guard: vessel context (not an action)
  const FERMENT_VESSEL = /\bfermentation (bucket|vessel)\b|\bfermenter\b/;
  // Actionful fermentation phrases
  const FERMENT_ACTION = /\b(primary fermentation|allow(ed)? to ferment|ferment(s|ing)? (at|for)\b)/;

  // Hygiene context that often appears alongside main actions
  const HYGIENE = [
    /\bsanitiz(e|ed|ing)?\b/,
    /\bstarsan\b/,
    /\bsteriliz(e|ed|ation)\b/,
    /\bclean(ing)?\b/,
    /\bwash(ing)?\b/,
  ];

  // Candidate scores
  type Row = { type: StepType; score: number };
  const rows: Row[] = [];

  const push = (type: StepType, cond: boolean | number) => {
    if (!cond) return;
    rows.push({ type, score: typeof cond === 'number' ? cond : 1 });
  };

  // --- main action detection (prioritize these) ---
  // Fermentation (require action; ignore mere vessel mentions)
  push('fermentation_primary', has(FERMENT_ACTION) && !/\bsecondary\b/.test(txt) ? 10 : 0);
  push('fermentation_secondary', /\bsecondary fermentation\b/.test(txt) ? 9 : 0);

  // Mixing / must creation
  push('mix', any([/\bmix(ing)?\b/, /\bcombine\b/, /\bdissolv(e|ed|ing)\b/, /\bmust\b/, /\bhoney\b/], txt) ? 8 : 0);

  // Yeast handling
  push('yeast_pitch', any([/\bpitch\b/, /\binoculat(e|ion)\b/, /\badd yeast\b/], txt) ? 8 : 0);
  push('yeast_rehydrate', any([/\brehydrat(e|ion)\b/, /\bgo-?ferm\b/], txt) ? 7 : 0);

  // Transfers / separation
  push('racking', any([/\brack(ing)?\b/, /\btransfer\b/], txt) ? 8 : 0);
  push('straining', any([/\bremove (the )?brew bag\b/, /\bstrain(ing)?\b/], txt) ? 7 : 0);
  push('filtering', /\bfilter(ing)?\b/.test(txt) ? 7 : 0);

  // Clarification / stabilization
  push('fining', any([/\bbentonite\b/, /\bsparkolloid\b/, /\bchitosan\b/, /\bfining\b/], txt) ? 7 : 0);
  push('cold_crash', /\bcold crash(ing)?\b/.test(txt) ? 7 : 0);
  push(
    'stabilize',
    any([/\bstabili[sz](e|ed|ing)\b/, /\bsorbate\b/, /\bk-?meta\b/, /\bcampden\b/, /\bsulfite(s)?\b/], txt) ? 7 : 0,
  );
  push('backsweeten', /\bbacksweet(en|ening)\b/.test(txt) ? 7 : 0);

  // Additions
  push('nutrient_addition', any([/\bnutrient(s)?\b/, /\btosna\b/, /\bsna\b/, /\bfermaid\b/], txt) ? 6 : 0);
  push('acid_adjust', any([/\bacid( adjust(ment)?)?\b/, /\bph\b/, /\bcitric\b/, /\btartaric\b/], txt) ? 6 : 0);
  push('tannin_add', /\btannin(s)?\b/.test(txt) ? 5 : 0);
  push('spice_add', any([/\bspice(s)?\b/, /\bcinnamon\b/, /\bvanilla\b/], txt) ? 5 : 0);
  push('fruit_add', any([/\bfruit(s)?\b/, /\bberries\b/, /\bcherry\b/, /\bblueberry\b/], txt) ? 6 : 0);
  push('oak_add', any([/\boak\b/, /\bcubes?\b/, /\bspirals?\b/, /\bchips?\b/], txt) ? 5 : 0);
  push('prepare', /\bprepare\b/.test(txt) ? 5 : 0);

  // Process control
  push(
    'temperature_control',
    any([/\btemp(erature)? (hold|ramp|control)\b/, /\bhold temp\b/, /\bramp temp\b/], txt) ? 5 : 0,
  );
  push('degassing', /\bdegass(ing)?\b/.test(txt) ? 5 : 0);
  push('step_feeding', any([/\bstep feed(ing)?\b/, /\bfeed honey\b/], txt) ? 6 : 0);

  // Packaging
  push('bottling', /\bbottl(e|ing|er|es)?\b/.test(txt) ? 9 : 0);
  push('carbonation_prime', any([/\bprim(e|ing)\b/], txt) ? 5 : 0);
  push('force_carb', /\bforce-?carbonat(e|ion)\b/.test(txt) ? 5 : 0);
  push('conditioning', any([/\bcondition(ing)?\b/, /\bbottle age(ing)?\b/], txt) ? 6 : 0);
  push('labeling', /\blabel(ing|led|s)?\b/.test(txt) ? 4 : 0);

  // QC
  push(
    'measurement',
    any(
      [/\bfinal testing\b/, /\bmeasure(ment|)\b/, /\bgravity\b/, /\bsg\b/, /\bplato\b/, /°p/, /\babv\b/, /\bph\b/],
      txt,
    )
      ? 7
      : 0,
  );
  push('sensory_check', any([/\btaste\b/, /\baroma\b/, /\bsensory\b/, /\borganoleptic\b/], txt) ? 5 : 0);

  // Detect "enjoy" or completion/finalization steps
  push('enjoy', /\benjoy\b/.test(txt) ? 5 : 0);
  push('enjoy', /\bcomplete\b/.test(txt) ? 5 : 0);
  push('enjoy', /\bfinish(ed|ing)?\b/.test(txt) ? 5 : 0);
  push('enjoy', /\bfinal(ly)?\b/.test(txt) ? 5 : 0);

  // Hygiene (only if nothing else meaningful)
  const hygieneHit = any(HYGIENE, txt);
  if (hygieneHit) {
    // If there's also a strong main action, downweight hygiene
    const hasMain =
      rows.some((r) => r.score >= 7) ||
      /\b(filter|bottl|rack|mix|ferment|secondary|backsweet|stabilis|fine|cold crash)\b/.test(txt);
    push('sanitize', hasMain ? 1 : 6);
    push('clean', hasMain ? 1 : 5);
  }

  // Fermentation guard: if only vessel mentioned, zero-out fermentation scores
  if (FERMENT_VESSEL.test(txt) && !FERMENT_ACTION.test(txt)) {
    for (const r of rows) {
      if (r.type === 'fermentation_primary' || r.type === 'fermentation_secondary') r.score = 0;
    }
  }

  // Choose best by score then by priority
  const PRIORITY: StepType[] = [
    'fermentation_primary',
    'fermentation_secondary',
    'mix',
    'yeast_pitch',
    'racking',
    'filtering',
    'fining',
    'cold_crash',
    'stabilize',
    'backsweeten',
    'bottling',
    'conditioning',
    'enjoy',
    'measurement',
    'fruit_add',
    'nutrient_addition',
    'acid_adjust',
    'temperature_control',
    'degassing',
    'step_feeding',
    'oak_add',
    'spice_add',
    'tannin_add',
    'carbonation_prime',
    'force_carb',
    'labeling',
    'straining',
    'yeast_rehydrate',
    'maceration',
    'oxygenate',
    'heat',
    'cool',
    'crush_puree',
    'juice_press',
    'sanitize',
    'clean',
    'maturation',
    'oak_age',
    'blend',
    'dry_addition',
    'qa_check',
    'sensory_check',
    'misc',
  ];

  const best = rows
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score || PRIORITY.indexOf(a.type) - PRIORITY.indexOf(b.type))[0];

  return best?.type ?? undefined;
}
